import { useTheme } from '@emotion/react';
import { IconArrowsVertical } from '@hubble/icons';
import React__default, { createContext, forwardRef, useMemo, useContext } from 'react';
import { Button } from '../../Button/index.js';
import { Message } from '../../forms/Message/index.js';
import { Text } from '../../Text/index.js';
import { InputCardWrapper, InputCardError, InputCardHeader, InputCardContent, InputCardInputGroup, InputCardPairGroup, InputCardFooter, InputCardInput, InputCardActionsWrapper, InputCardLabel, InputCardUtility } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';

const InputCardContext = createContext({
  name: ''
});
const InputCardRoot = forwardRef(({
  name,
  error,
  children,
  ...props
}, ref) => {
  const contextValue = useMemo(() => ({
    name
  }), [name]);
  let utilityComponent = null;
  const otherChildren = [];
  React__default.Children.forEach(children, child => {
    if (React__default.isValidElement(child) && child.type === Utility) {
      utilityComponent = child;
    } else {
      otherChildren.push(child);
    }
  });
  return jsx(InputCardContext.Provider, {
    value: contextValue,
    children: jsxs("div", {
      ref: ref,
      ...props,
      children: [jsx(InputCardWrapper, {
        htmlFor: name,
        "data-error": Boolean(error),
        children: otherChildren
      }), error && jsx(InputCardError, {
        children: jsx(Message, {
          error: error
        })
      }), utilityComponent]
    })
  });
});
InputCardRoot.displayName = 'InputCard';
const Header = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardHeader, {
    ref: ref,
    ...props,
    children: children
  });
});
Header.displayName = 'InputCard.Header';
const Content = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardContent, {
    ref: ref,
    ...props,
    children: children
  });
});
Content.displayName = 'InputCard.Content';
const InputGroup = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardInputGroup, {
    ref: ref,
    ...props,
    children: children
  });
});
InputGroup.displayName = 'InputCard.InputGroup';
const PairGroup = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardPairGroup, {
    ref: ref,
    ...props,
    children: children
  });
});
PairGroup.displayName = 'InputCard.PairGroup';
const Footer = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardFooter, {
    ref: ref,
    ...props,
    children: children
  });
});
Footer.displayName = 'InputCard.Footer';
const Input = forwardRef((props, ref) => {
  const contextValue = useContext(InputCardContext);
  const contextName = contextValue.name;
  const name = props.name || contextName;
  const handleWheel = event => {
    if (event.currentTarget.type === 'number') {
      event.currentTarget.blur();
    }
  };
  return jsx(InputCardInput, {
    ...props,
    as: "input",
    name: name,
    id: name,
    size: "lg",
    onWheel: props.onWheel || handleWheel,
    ref: ref
  });
});
Input.displayName = 'InputCard.Input';
const Actions = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardActionsWrapper, {
    ref: ref,
    ...props,
    children: children
  });
});
Actions.displayName = 'InputCard.Actions';
const Pair = forwardRef(({
  children,
  onSwap,
  ...props
}, ref) => {
  const theme = useTheme();
  const SWAP_ARIA_LABEL = theme.translate('InputCard.swapButton');
  return jsxs(InputCardLabel, {
    flex: "1 0 0",
    ref: ref,
    ...props,
    children: [typeof children === 'string' ? jsx(Text.Body, {
      as: "span",
      size: "sm",
      bold: true,
      color: "currentColor",
      children: children
    }) : children, onSwap && jsx(Button.Quaternary, {
      onClick: onSwap,
      size: "xs",
      "aria-label": SWAP_ARIA_LABEL,
      icon: jsx(IconArrowsVertical, {})
    })]
  });
});
Pair.displayName = 'InputCard.Pair';
const Constraint = forwardRef(({
  children,
  leftElement,
  ...props
}, ref) => {
  const theme = useTheme();
  return jsx(InputCardLabel, {
    children: jsx(Button.Quaternary, {
      size: "xs",
      ref: ref,
      leftElement: leftElement,
      ...props,
      children: typeof children === 'string' ? jsx(Text.Body, {
        size: "xs",
        bold: true,
        color: theme.colorScheme.content.secondary,
        children: children
      }) : children
    })
  });
});
Constraint.displayName = 'InputCard.Constraint';
const Utility = forwardRef(({
  children,
  ...props
}, ref) => {
  return jsx(InputCardUtility, {
    ref: ref,
    ...props,
    children: children
  });
});
Utility.displayName = 'InputCard.Utility';
const ComposableInputCard = Object.assign(InputCardRoot, {
  Header,
  Content,
  InputGroup,
  PairGroup,
  Footer,
  Input,
  Actions,
  Pair,
  Constraint,
  Utility
});

export { ComposableInputCard };
